/**
 * Centralized validation utilities for the application
 */

/**
 * UUID v4 regex pattern for validation
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validates if a string is a valid UUID v4 format
 * @param value - The string to validate
 * @returns true if valid UUID, false otherwise
 */
export const isValidUUID = (value: string | null | undefined): boolean => {
  if (!value || typeof value !== "string") return false;
  return UUID_PATTERN.test(value);
};

/**
 * Ensures a value is a boolean, with a default fallback
 * @param value - The value to validate
 * @param defaultValue - Default value if validation fails
 * @returns A valid boolean value
 */
export const ensureBoolean = (
  value: unknown,
  defaultValue: boolean = false
): boolean => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return defaultValue;
};

/**
 * Ensures a value is a string, with a default fallback
 * @param value - The value to validate
 * @param defaultValue - Default value if validation fails
 * @returns A valid string value
 */
export const ensureString = (
  value: unknown,
  defaultValue: string = ""
): string => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return defaultValue;
  return String(value);
};

/**
 * Ensures a value is a number, with a default fallback
 * @param value - The value to validate
 * @param defaultValue - Default value if validation fails
 * @returns A valid number value
 */
export const ensureNumber = (
  value: unknown,
  defaultValue: number = 0
): number => {
  if (typeof value === "number" && !isNaN(value)) return value;
  const parsed = Number(value);
  if (!isNaN(parsed)) return parsed;
  return defaultValue;
};

/**
 * Trims and validates a string, returning null if empty
 * @param value - The string to process
 * @returns Trimmed string or null if empty
 */
export const trimOrNull = (value: string | null | undefined): string | null => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
